import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import React from 'react'

import { cn } from '@/lib/cn'

import type { ButtonProps } from '../button'

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
)
Pagination.displayName = 'Pagination'

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center gap-7', className)}
    {...props}
  />
))
PaginationContent.displayName = 'PaginationContent'

const paginationItemVariants = cva('cursor-pointer', {
  variants: {
    isDisabled: {
      true: 'cursor-not-allowed opacity-50',
      false: '',
    },
  },
  defaultVariants: {
    isDisabled: false,
  },
})

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'> & VariantProps<typeof paginationItemVariants>
>(({ className, isDisabled, ...props }, ref) => (
  <li
    ref={ref}
    className={cn(paginationItemVariants({ className, isDisabled }))}
    {...props}
  />
))
PaginationItem.displayName = 'PaginationItem'

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<'a'>

const paginationLinkVariants = cva(
  'inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-lg font-normal transition-colors focus:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-system-selected-blue  disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-[0.36]',
  {
    variants: {
      size: {
        default: 'h-10 px-3 py-2 text-sm',
        sm: 'h-9 px-2 py-1 text-xs',
        lg: 'h-11 px-8',
        icon: 'size-10',
        link: 'h-auto text-sm',
      },
      variant: {
        default: 'border-none bg-transparent text-system-text-secondary',
        active:
          'border-system-border-primary bg-white text-system-text-secondary',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const PaginationLink = ({
  className,
  isActive,
  size = 'icon',
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      paginationLinkVariants({
        variant: isActive ? 'active' : 'default',
        size,
      }),
      'rounded-sm border border-system-border-primary px-2 py-1  text-system-text-secondary text-sm font-medium size-7',
      className,
    )}
    {...props}
  />
)
PaginationLink.displayName = 'PaginationLink'

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="default"
    className={cn(
      'gap-1 border-0 size-fit p-0 flex items-center bg-transparent',
      className,
    )}
    {...props}
  >
    <ChevronLeft className="size-4" />
    <span>Anterior</span>
  </PaginationLink>
)
PaginationPrevious.displayName = 'PaginationPrevious'

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    size="default"
    className={cn(
      'gap-1 border-0 size-fit p-0 flex items-center bg-transparent',
      className,
    )}
    {...props}
  >
    <span>Próximo</span>
    <ChevronRight className="size-4" />
  </PaginationLink>
)
PaginationNext.displayName = 'PaginationNext'

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn(
      'flex size-fit items-center justify-center text-system-text-secondary',
      className,
    )}
    {...props}
  >
    <MoreHorizontal className="size-4" />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = 'PaginationEllipsis'

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
